import moment from "moment";
import { isHlsStream, StreamType } from "@telus/opus-component-library/video-player";
import telusConvivaAnalytics from "../../../shared/utils/convivaAnalytics";
import { setLocalStorage } from "../../../shared/utils/localStorage";
import { getAudioTrackId } from "../../../shared/utils/playerUtils";
import constants from "../../../shared/constants";
import PlayerConstants from "../../../shared/constants/player";
import storageConstants from "../../../shared/constants/storage";
import { getLocalStorage, removeLocalStorage } from "../../../shared/utils/localStorage";
import { logDTEvent } from "../../../shared/analytics/dynatrace";
import { DT_ATTRIBUTES, DT_EVENTS } from "../../../shared/constants/dynatrace";
const { DRM_SYSTEM_TYPES } = PlayerConstants;
const { VIDEO_AUTH_TOKEN } = storageConstants;

const { SETTINGS_CONFIG } = constants;

export const playerGetDuration = (playerMgr) => {
  return playerMgr.current?.getDuration();
};

export const playerCheckDynamicAsset = (playerMgr) => {
  return playerMgr.current?.isLive() || playerMgr.current?.isInProgress();
};

export const playerGetRecordingStartOffset = (playerMgr, hasRecordingDeltaTime, recordingDeltaTime) => {
  let startOffset = 0; // init offset to 0
  if (hasRecordingDeltaTime && playerMgr.current) {
    // deltaTime is set so this is playing recording asset
    startOffset =
      -(moment().valueOf() - (recordingDeltaTime.recordingStartTime + recordingDeltaTime.startDeltaTime)) / 1000; // set offset to user specific recording startDeltaTime
    const maxTimeShift = playerMgr.current.getMaxTimeShift(); // For dynamic asset, each live stream playback has a max limit in seconds for time shift. In our case (PLTV feature), current limit is set to 3560 seconds, this could change in the future
    startOffset = Math.abs(startOffset) < Math.abs(maxTimeShift) ? startOffset : maxTimeShift; // if the user's specific recording offset value is greater than max shift limit, we have to set offset to the max shift limit instead
  }
  return startOffset;
};

/**
 * Builds a source config object to be used with the player component.
 * Supports DASH + Widevine or clear DASH/HLS streams only.
 *
 * @param {Object} streamInfo Object containing stream information
 * @param {Function} handleManifestResponse Callback function to handle manifest responses
 * @param {Function} handleLicenseRequest Callback function to handle license requests
 * @param {Boolean} isFast Flag indicating if the stream is a fast channel
 * @returns {Object|null} Source config object or null if streamInfo is not provided
 */

export const shakaBuildPlayerSourceConfig = (
  manifestUrl,
  jwtToken,
  contentId,
  licenseUrl,
  certificateUrl,
  handleManifestResponse,
  handleLicenseRequest,
  isLivePlayer = false,
  isFast = false
) => {
  if (!manifestUrl) return null;
  const id = contentId;
  const url = manifestUrl;
  const source = {
    id,
    type: isHlsStream(manifestUrl) ? StreamType.HLS : StreamType.DASH,
    url,
    interceptManifestResponse: handleManifestResponse,
    drm: undefined,
    autoplay: true,
  };
  const cachedAuthToken = isLivePlayer ? getLocalStorage(VIDEO_AUTH_TOKEN) : null;
  const authToken = jwtToken ? jwtToken : cachedAuthToken ? cachedAuthToken : null;
  // Clear stale cached token so it isn't reused by a subsequent channel's source config
  if (isLivePlayer && authToken) {
    removeLocalStorage(VIDEO_AUTH_TOKEN);
  }
  if (!isFast && authToken) {
    const isHLS = isHlsStream(url);
    source.drm = {
      type: isHLS ? DRM_SYSTEM_TYPES.FP : DRM_SYSTEM_TYPES.WV,
      licenseUrl: licenseUrl,
      withCredentials: true,
      headers: {
        Authorization: authToken,
        "Content-Type": isHLS ? "application/json" : "application/octet-stream",
      },
      interceptRequest: handleLicenseRequest,
    };
    if (isHLS) {
      source.drm.certificateUrl = certificateUrl;
    }
  }
  source.streaming = {
    forceHTTPS: true,
  };
  return source;
};

export const playerExists = (playerMgr) => {
  return playerMgr && playerMgr.current !== null;
};

export const shakaGetHlsLivePlayheadTs = (playerMgr) => {
  return Math.min(moment(playerMgr.current?.getPlayheadTimeAsDate()).valueOf(), moment().valueOf());
};

export const playerGetCurrentPlaybackTime = (playerMgr, currentManifestUrl = null) => {
  if (playerMgr.current?.isLive() && currentManifestUrl) {
    const isHls = isHlsStream(currentManifestUrl);
    return (
      !playerMgr.current?.destroyed &&
      (isHls ? shakaGetHlsLivePlayheadTs(playerMgr) : playerMgr.current?.getCurrentTime() * 1000)
    );
  }
  return !playerMgr.current?.destroyed && playerMgr.current?.getCurrentTime() * 1000;
};

export const playerHandleBookmarkResume = (playerMgr, resumeTimeSeconds, recordingStartDeltaTime) => {
  let initPlayHead = 0;

  // Bookmark resume - VOD playback resume point is relative to 0 point of playback, recording playback resume point is relative to user's recordingStartDeltaTime
  if (resumeTimeSeconds && resumeTimeSeconds > 0) {
    initPlayHead = recordingStartDeltaTime !== null ? recordingStartDeltaTime + resumeTimeSeconds : resumeTimeSeconds;
  }

  if (initPlayHead > 0) playerMgr.current?.seek(initPlayHead); // Note: for live stream, seek is ignored, so this does nothing
};

export const playerInitDynamicPartialRecording = (
  playerMgr,
  isRecording,
  userRecordingStartTime,
  isDynamicRecordingType,
  goLive,
  bookmark,
  shouldResumeFromBookmark
) => {
  if (isRecording && playerMgr.current.isLive()) {
    const bookmarkStartDeltaTime = bookmark?.startDeltaTime || 0;
    // For dynamic partial recording playback, we need to shift the live stream to the user's recording start time
    // When a recording is resumed, the live stream should seek to the user’s last bookmarked point.
    playerMgr.current.timeShift(userRecordingStartTime / 1000 + bookmarkStartDeltaTime);
    isDynamicRecordingType.current = true;
    goLive.current = 1;
    shouldResumeFromBookmark.current = false;
  }
};

export const shakaInitHlsDynamicPartialRecording = (
  playerMgr,
  isRecording,
  recordingStartDeltaTime,
  isDynamicRecordingType,
  goLive
) => {
  if (isRecording && playerMgr.current.isLive()) {
    // For dynamic partial recording playback, we need to shift the live stream to the user's recording start time
    playerMgr.current.timeShift(recordingStartDeltaTime);
    isDynamicRecordingType.current = true;
    goLive.current = 1;
  }
};

export const playerHandleLoadPlaybackSuccess = (propsRef, props, cdnUrl, playerMgr) => {
  propsRef.current = { ...props, cdnUrl: cdnUrl.current };
  telusConvivaAnalytics.onLoadPlayerSuccessful(propsRef.current, playerMgr.current);
};

export const shakaGetCurrentTime = (playerMgr) => {
  return playerMgr.current?.getCurrentTime();
};

export const shakaGetAvailableAudio = (playerMgr) => {
  return playerMgr.current?.getAudioTracks();
};

export const shakaSetAudio = (playerMgr, audioTrackId) => {
  playerMgr.current?.setAudioTrack(audioTrackId);
};

export const playerIsStalled = (playerMgr) => {
  return playerMgr.current?.isStalled();
};

export const playerIsPlaying = (playerMgr) => {
  return playerMgr.current?.isPlaying();
};

export const playerHandleADToggleChange = (
  isADEnabled,
  isDescribedVideoEnabled,
  playerMgr,
  signalUserActivity,
  describedVideo,
  isDynatraceTrackingEnabled
) => {
  isDescribedVideoEnabled.current = isADEnabled;
  setLocalStorage(SETTINGS_CONFIG.DESCRIBED_VIDEO, isADEnabled);

  const availableAudioTracks = shakaGetAvailableAudio(playerMgr);
  if (availableAudioTracks) {
    describedVideo.current = 1;
    shakaSetAudio(playerMgr, getAudioTrackId(availableAudioTracks, isADEnabled));
    logDTEvent(DT_EVENTS.TOGGLE_DV, { [DT_ATTRIBUTES.TOGGLE_ON]: isADEnabled }, isDynatraceTrackingEnabled);
  }
  signalUserActivity();
};
